import { Button, ProgressTrack, Skeleton } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { LoadFailed } from '../LoadFailed.tsx';
import { usePageContext } from '../usePageContext.ts';
import { setupHrefOf } from './setup-path.ts';
import { useSetupPreferences } from './setup-preferences.ts';
import { resumeStepOf, stepIndexOf, useSetupProgress } from './setup-progress.ts';

type CompletedSetup = 'review' | 'hide';

export type SetupProgressCardProps = {
  api: TypedClient;
  shipping: ShippingClient;
  whenComplete: CompletedSetup;
  onOpenStep: (step: number) => void;
};

export const SetupProgressCard = ({ api, shipping, whenComplete, onOpenStep }: SetupProgressCardProps) => {
  const { t, describeError } = usePageContext();
  const progress = useSetupProgress(api, shipping);
  const expanded = useSetupPreferences((state) => state.trackExpanded);
  const toggle = useSetupPreferences((state) => state.toggleTrack);
  if (progress.state === 'loading') return <Skeleton height="136px" label={t('setup.trackTitle')} />;
  if (progress.state === 'failed')
    return <LoadFailed description={describeError(progress.error)} onRetry={progress.retry} />;
  const remaining = progress.steps.filter((step) => step.status !== 'done').length;
  if (remaining === 0 && whenComplete === 'hide') return null;
  const done = progress.steps.length - remaining;
  const targets = new Map<string, number>(progress.steps.map((step) => [step.id, stepIndexOf(step.id)]));
  const resume = stepIndexOf(resumeStepOf(progress.steps));
  return (
    <ProgressTrack
      title={t('setup.trackTitle')}
      summary={remaining === 0 ? t('setup.trackSummaryDone') : t('setup.trackSummary', { count: remaining })}
      progressLabel={t('setup.trackProgress', { done, total: progress.steps.length })}
      steps={progress.steps.map((step) => ({
        id: step.id,
        status: step.status,
        title: t(`setup.step_${step.id}`),
        description: t(`setup.step_${step.id}Card`),
      }))}
      expanded={expanded}
      toggleLabel={t('setup.trackToggle')}
      onToggle={toggle}
      action={
        <Button
          size="sm"
          variant={remaining === 0 ? 'outline' : 'primary'}
          onClick={() => {
            onOpenStep(resume);
          }}
        >
          {remaining === 0 ? t('setup.trackReview') : t('setup.trackResume')}
        </Button>
      }
      renderStep={(step, content) => {
        const target = targets.get(step.id) ?? resume;
        return (
          <a
            href={setupHrefOf(target)}
            onClick={(event) => {
              event.preventDefault();
              onOpenStep(target);
            }}
          >
            {content}
          </a>
        );
      }}
    />
  );
};
